import { reactive } from 'vue'
import DemopartyClient from '@/scripts/services/demoparty'
import { initDB, getFile, saveFile, deleteFile } from '@/scripts/services/IndexedDBService'
import { uuidv4 } from '@/scripts/uuid'
import eventBus from '@/scripts/utils/eventBus'
import { getUserErrorDescription, getUserErrorMessage } from '@/scripts/utils/errorReporting'
import { settings } from '@/scripts/services/SettingsService'

class StorageService {
    constructor() {
        // Track created Blob URLs to prevent memory leaks
        this._blobUrls = new Set()

        // Cache for signed URLs (fileId -> { url, expiresAt })
        // TTL is 5 hours (server signs for 6 hours, we use less for safety margin)
        this._urlCache = new Map()
        this._urlCacheTTL = 5 * 60 * 60 * 1000 // 5 hours in ms

        // Track index for O(1) lookup by ID
        this._trackIndex = new Map()

        this.state = reactive({
            projects: [],
            tracks: [],
            presets: [],
            user: null,
            agents: [],
            subscription: [],
            storageType: 'local',
            sharedTracks: [],
            sharedProjects: [],

            isAuthorized: false,
            token: null,
            client: null,
            userId: null,
            username: null,
            isLoading: false,
            dataLoaded: false,
            lastError: null,
            publicLinks: [],
            trackPagination: {
                nextCursor: null,
                hasMore: false,
                isLoading: false,
                error: null,
                initialized: false
            },
            searchResults: { projects: [], tracks: [] },
            searchQuery: '',
            searchLimit: 5
        })

        this.state.storageType = localStorage.getItem('demoparty_storage_type') ?? 'local'
        this.state.token = localStorage.getItem('demoparty_token') ?? null
        this.state.userId = localStorage.getItem('demoparty_user_id') ?? null
        this.state.username = localStorage.getItem('demoparty_username') ?? null
        this.hydrateUserFromStorage()
        this.checkAuth()

        // Initialize client if we have a token (for API access regardless of storage mode)
        if (this.state.token) {
            this.state.client = new DemopartyClient('https://meow.demoparty.app', this.state.token, this.state.userId, this.state.username)
        }

        this._searchRequestId = 0
        this._searchDebounceTimer = null
        this._profileLoadPromise = null
        this._profileHydratedFromRemote = false
        this._presetsLoadPromise = null
        this._tracksPageRequestId = 0
    }

    hydrateUserFromStorage() {
        const userIdInStorage = localStorage.getItem('demoparty_user_id') ?? null
        const usernameInStorage = localStorage.getItem('demoparty_username') ?? null
        if (userIdInStorage || usernameInStorage) {
            this.state.userId = userIdInStorage
            this.state.username = usernameInStorage
            this.state.user = { id: userIdInStorage, username: usernameInStorage }
        }
    }

    applyUserProfile(data) {
        if (!data) return
        this._profileHydratedFromRemote = true

        const user = data.user || null
        if (user) {
            this.state.user = user
            if (user.id) {
                this.state.userId = user.id
                localStorage.setItem('demoparty_user_id', user.id)
            }
            if (user.username) {
                this.state.username = user.username
                localStorage.setItem('demoparty_username', user.username)
            }
            if (this.state.client) {
                this.state.client.setUserId(this.state.userId)
                this.state.client.setUsername(this.state.username)
            }
        }

        this.state.subscription = data.subscription ?? this.state.subscription
        this.state.agents = data.agents || this.state.agents
        this.state.publicLinks = data.public_links || this.state.publicLinks
        if (this.state.client) {
            settings.syncWithCloud(this.state.client, data.settings)
        }
    }

    getProjectSortOrder(project, fallbackSortOrder = null) {
        const rawSortOrder = project?.sort_order ?? project?.sortOrder ?? fallbackSortOrder
        const parsedSortOrder = Number.isFinite(rawSortOrder)
            ? rawSortOrder
            : Number.parseInt(rawSortOrder, 10)
        return Number.isFinite(parsedSortOrder) ? parsedSortOrder : null
    }

    getProjectPublicValue(project, fallback = false) {
        const value = project?.isPublic ?? project?.public ?? fallback
        if (typeof value === 'string') {
            return value.toLowerCase() === 'true'
        }
        return Boolean(value)
    }

    normalizeProject(project, fallbackSortOrder = null) {
        const tracks = this.normalizeTracks(Array.isArray(project?.tracks) ? project.tracks : [])
        const rawCount = project?.track_count
        const trackCount = Number.isFinite(rawCount) ? rawCount : Number.parseInt(rawCount, 10)
        const sortOrder = this.getProjectSortOrder(project, fallbackSortOrder)
        return {
            ...project,
            tracks,
            track_count: Number.isFinite(trackCount) ? trackCount : tracks.length,
            public: this.getProjectPublicValue(project),
            sort_order: sortOrder
        }
    }

    sortProjects(projects = []) {
        return [...projects].sort((a, b) => {
            const sortOrderA = this.getProjectSortOrder(a) ?? Number.MAX_SAFE_INTEGER
            const sortOrderB = this.getProjectSortOrder(b) ?? Number.MAX_SAFE_INTEGER
            if (sortOrderA !== sortOrderB) {
                return sortOrderA - sortOrderB
            }
            const nameA = (a?.name || '').toLowerCase()
            const nameB = (b?.name || '').toLowerCase()
            return nameA.localeCompare(nameB) || String(a?.id || '').localeCompare(String(b?.id || ''))
        })
    }

    normalizeProjects(projects = []) {
        const normalized = projects.map((project, index) => this.normalizeProject(project, index + 1))
        return this.sortProjects(normalized)
    }

    normalizeTrack(track) {
        if (!track) return null

        const parseFiniteInt = (value) => {
            const parsed = Number.isFinite(value) ? value : Number.parseInt(value, 10)
            return Number.isFinite(parsed) ? parsed : null
        }

        return {
            ...track,
            id: track.id,
            name: track.name ?? track.filename ?? 'Untitled',
            artist: track.artist ?? 'Unknown Artist',
            notes: track.notes ?? '',
            effects: track.effects ?? '',
            num: track.num ?? null,
            cover: track.cover ?? null,
            file: track.file ?? null,
            waveform: track.waveform ?? null,
            bpm: parseFiniteInt(track.bpm),
            key: track.key ?? null,
            mode: track.mode ?? null,
            duration: parseFiniteInt(track.duration),
            lufs: parseFiniteInt(track.lufs),
            upload_date: track.upload_date ?? null
        }
    }

    normalizeTracks(tracks = []) {
        return tracks
            .map(track => this.normalizeTrack(track))
            .filter(Boolean)
    }

    resequenceProjects(projects = []) {
        return projects.map((project, index) => this.normalizeProject({
            ...project,
            sort_order: index + 1
        }, index + 1))
    }

    hasLoadedProjectTracks(project) {
        if (!project) return false
        const tracks = Array.isArray(project.tracks) ? project.tracks : []
        const trackCount = Number.isFinite(project.track_count)
            ? project.track_count
            : (Number.parseInt(project.track_count, 10) || tracks.length)
        return tracks.length > 0 || trackCount === 0
    }

    async ensureProjectTracksLoaded(projectId) {
        const project = this.state.projects.find(p => p.id === projectId)
        if (!project) return null
        if (this.state.storageType !== 'remote') return project
        if (this.hasLoadedProjectTracks(project)) {
            return project
        }
        return await this.loadSingleProject(projectId)
    }

    async checkAuth() {
        // Auth is independent of storage mode - user can be logged in while using local storage
        const tokenInStorage = localStorage.getItem('demoparty_token') ?? null
        const userIdInStorage = localStorage.getItem('demoparty_user_id') ?? null
        const usernameInStorage = localStorage.getItem('demoparty_username') ?? null

        if (this.state.token || tokenInStorage) {
            this.state.token = this.state.token || tokenInStorage
            if (userIdInStorage || usernameInStorage) {
                this.state.userId = userIdInStorage
                this.state.username = usernameInStorage
                if (!this.state.user || !this.state.user.email) {
                    this.state.user = { id: userIdInStorage, username: usernameInStorage }
                }
            }
            this.state.isAuthorized = true
            // Ensure client is available for API calls (subscription, storage info, etc.)
            if (!this.state.client) {
                this.state.client = new DemopartyClient('https://meow.demoparty.app', this.state.token, this.state.userId, this.state.username)
            }
            return true
        }

        this.hydrateUserFromStorage()
        this.state.isAuthorized = false
        return false
    }

    async loadUserProfile({ force = false } = {}) {
        await this.checkAuth()
        if (!this.state.isAuthorized || !this.state.token) {
            return false
        }
        if (!force && this._profileHydratedFromRemote && this.state.user?.username) {
            return true
        }
        if (this._profileLoadPromise) {
            return this._profileLoadPromise
        }

        this._profileLoadPromise = (async () => {
            try {
                if (!this.state.client) {
                    this.state.client = new DemopartyClient('https://meow.demoparty.app', this.state.token, this.state.userId, this.state.username)
                }
                const data = await this.state.client.getUserProfile('me', { includeTracks: false })
                this.applyUserProfile(data)
                return true
            } catch (error) {
                console.warn('Failed to load user profile:', error)
                return false
            } finally {
                this._profileLoadPromise = null
            }
        })()

        return this._profileLoadPromise
    }

    logout() {
        console.log("Logout")
        // Revoke all Blob URLs to prevent memory leaks
        this.revokeAllBlobUrls()
        // Clear URL cache
        this.clearUrlCache()
        this.state.token = null
        this.state.userId = null
        this.state.username = null
        this.state.user = null
        this.state.subscription = null
        this.state.client = null
        this._profileHydratedFromRemote = false
        settings.configureCloudSync(null)
        this.resetTrackPagination()
        this.state.isAuthorized = false
        this.state.storageType = 'local'
        localStorage.removeItem('demoparty_token')
        localStorage.removeItem('demoparty_user_id')
        localStorage.removeItem('demoparty_username')
        localStorage.setItem('demoparty_storage_type', 'local')
        this.checkAuth()
        this.loadData()
        eventBus.$emit('logout')
        eventBus.notify('Logout', 'You have been logged out. Switched to local storage.');
        return true
    }

    async loadData() {
        console.log("Loading Data")
        this.state.isLoading = true
        await this.loadPresets()
        try {
            if (this.state.storageType === 'local') {
                console.log("Loading Local Data")
                this.state.projects = this.normalizeProjects(JSON.parse(localStorage.getItem('projects')) || [])
                this.state.tracks = this.normalizeTracks(JSON.parse(localStorage.getItem('tracks')) || [])
                this.resetTrackPagination()
                localStorage.setItem('tracks', JSON.stringify(this.state.tracks))
                localStorage.setItem('projects', JSON.stringify(this.state.projects))
                await initDB()
                this._rebuildTrackIndex()
                // If user is authorized, load user profile and subscription from server
                if (this.state.isAuthorized && this.state.client) {
                    try {
                        const data = await this.state.client.getUserProfile('me', { includeTracks: false })
                        this.applyUserProfile(data)
                    } catch (error) {
                        console.warn('Failed to load user profile in local mode:', error)
                        // Keep existing user data from localStorage
                    }
                }
                this.state.isLoading = false
                this.state.dataLoaded = true
                return true
            }
            if (this.state.storageType === 'remote' && this.state.isAuthorized) {
                console.log("Loading Remote Data")
                this.state.client = new DemopartyClient('https://meow.demoparty.app', this.state.token, this.state.userId, this.state.username)
                const [data] = await Promise.all([
                    this.state.client.getUserProfile('me', { includeTracks: false }),
                    this.loadStandaloneTracks({ reset: true })
                ])
                this.applyUserProfile(data)
                this.state.user = data['user'] || []
                this.state.projects = this.normalizeProjects(data['projects'] || [])
                this.state.publicLinks = data['public_links'] || []
                this.state.agents = data['agents'] || []
                this.state.subscription = data['subscription'] || null
                this._rebuildTrackIndex()
                this.state.isLoading = false
                this.state.dataLoaded = true
                return true
            }
            console.log("Loading Failed")
            this.state.isLoading = false
            return false
        } catch (error) {
            this.logError(error, 'loadData')
            this.state.storageType = 'local'
            this.state.token = null,
            this.state.userId = null
            this.state.username = null
            this.state.isAuthorized = false
            this.state.isLoading = false
            return false
        }
    }

    resetTrackPagination() {
        this._tracksPageRequestId += 1
        this.state.trackPagination.nextCursor = null
        this.state.trackPagination.hasMore = false
        this.state.trackPagination.isLoading = false
        this.state.trackPagination.error = null
        this.state.trackPagination.initialized = false
    }

    async loadStandaloneTracks({ reset = false } = {}) {
        if (this.state.storageType !== 'remote' || !this.state.isAuthorized) return []
        if (this.state.trackPagination.isLoading) return []
        if (!reset && this.state.trackPagination.initialized && !this.state.trackPagination.hasMore && !this.state.trackPagination.error) return []

        if (!this.state.client) {
            this.state.client = new DemopartyClient('https://meow.demoparty.app', this.state.token, this.state.userId, this.state.username)
        }

        const requestId = ++this._tracksPageRequestId
        const cursor = reset ? null : this.state.trackPagination.nextCursor
        this.state.trackPagination.isLoading = true
        this.state.trackPagination.error = null

        try {
            const response = await this.state.client.getStandaloneTracks({ limit: 30, cursor })
            if (requestId !== this._tracksPageRequestId) return []

            const incoming = this.normalizeTracks(response?.tracks || [])
            const existing = reset ? [] : this.state.tracks
            const byId = new Map(existing.map(track => [track.id, track]))
            const added = []
            for (const track of incoming) {
                if (!byId.has(track.id)) added.push(track)
                byId.set(track.id, track)
            }

            this.state.tracks = [...byId.values()].sort((left, right) => {
                const dateDiff = new Date(right.upload_date || right.created_at || 0) - new Date(left.upload_date || left.created_at || 0)
                if (dateDiff !== 0) return dateDiff
                return String(right.id || '').localeCompare(String(left.id || ''))
            })
            this.state.trackPagination.nextCursor = response?.pagination?.next_cursor || null
            this.state.trackPagination.hasMore = Boolean(response?.pagination?.has_more)
            this.state.trackPagination.initialized = true
            this._rebuildTrackIndex()
            eventBus.$emit('standalone-tracks-loaded', added)
            return added
        } catch (error) {
            if (requestId === this._tracksPageRequestId) {
                this.state.trackPagination.error = getUserErrorDescription(error, 'loadStandaloneTracks')
                this.state.trackPagination.initialized = true
            }
            return []
        } finally {
            if (requestId === this._tracksPageRequestId) {
                this.state.trackPagination.isLoading = false
            }
        }
    }

    async loadPresets() {
        if (this._presetsLoadPromise) {
            return this._presetsLoadPromise
        }

        this._presetsLoadPromise = (async () => {
            const storedPresets = localStorage.getItem('presets')
            if (storedPresets) {
                try {
                    this.state.presets = JSON.parse(storedPresets)
                    return true
                } catch (error) {
                    console.error("Failed to parse stored presets:", error)
                    localStorage.removeItem('presets')
                }
            }

            try {
                const response = await fetch('/default_presets.json')
                if (response.ok) {
                    const defaultPresets = await response.json()
                    this.state.presets = defaultPresets
                    localStorage.setItem('presets', JSON.stringify(defaultPresets))
                    console.log("Loaded default presets from file")
                    return true
                } else {
                    this.state.presets = []
                    return false
                }
            } catch (error) {
                console.error("Failed to load default presets:", error)
                this.state.presets = []
                return false
            }
        })()

        try {
            return await this._presetsLoadPromise
        } finally {
            this._presetsLoadPromise = null
        }
    }

    // Lazy loading: Load single track by ID (for TrackView)
    async loadSingleTrack(trackId) {
        console.log("Loading single track", trackId)
        if (!trackId) return null

        // First check if track already exists in state
        const existingTrack = this.getTrackById(trackId)
        if (existingTrack) {
            return existingTrack
        }

        // For local storage, track should already be in state after loadData
        if (this.state.storageType === 'local') {
            return null
        }

        // For remote storage, fetch from API
        if (this.state.storageType === 'remote' && this.state.isAuthorized) {
            try {
                if (!this.state.client) {
                    this.state.client = new DemopartyClient('https://meow.demoparty.app', this.state.token, this.state.userId, this.state.username)
                }
                const response = await this.state.client.getTrack(trackId)
                if (response && response.track) {
                    const normalizedTrack = this.normalizeTrack(response.track)
                    if (existingTrack) {
                        this._replaceTrackAcrossState(normalizedTrack)
                    } else {
                        this.state.tracks.push(normalizedTrack)
                    }
                    this._rebuildTrackIndex()
                    return this.getTrackById(normalizedTrack.id) || normalizedTrack
                }
                return null
            } catch (error) {
                this.logError(error, 'loadSingleTrack')
                return null
            }
        }
        return null
    }

    // Lazy loading: Load single project by ID (for ProjectView)
    async loadSingleProject(projectId) {
        console.log("Loading single project", projectId)
        if (!projectId) return null

        // First check if project already exists in state
        const existingProject = this.state.projects.find(p => p.id === projectId)
        if (existingProject) {
            if (this.state.storageType !== 'remote') {
                return existingProject
            }
            const trackCount = Number.isFinite(existingProject.track_count)
                ? existingProject.track_count
                : (existingProject.tracks || []).length
            const needsFetch = !Array.isArray(existingProject.tracks)
                || (existingProject.tracks.length === 0 && trackCount > 0)
                || existingProject.artist == null
            if (!needsFetch) {
                return existingProject
            }
        }

        // For local storage, project should already be in state after loadData
        if (this.state.storageType === 'local') {
            return null
        }

        // For remote storage, fetch from API
        if (this.state.storageType === 'remote' && this.state.isAuthorized) {
            try {
                if (!this.state.client) {
                    this.state.client = new DemopartyClient('https://meow.demoparty.app', this.state.token, this.state.userId, this.state.username)
                }
                const response = await this.state.client.getProject(projectId)
                if (response && response.project) {
                    const normalizedProject = this.normalizeProject(response.project)
                    const nextProjects = [...this.state.projects]
                    const projectIndex = this.state.projects.findIndex(p => p.id === response.project.id)
                    if (projectIndex === -1) {
                        nextProjects.push(normalizedProject)
                    } else {
                        nextProjects.splice(projectIndex, 1, normalizedProject)
                    }
                    this.state.projects = this.normalizeProjects(nextProjects)
                    return this.state.projects.find(project => project.id === normalizedProject.id) || null
                }
                return null
            } catch (error) {
                this.logError(error, 'loadSingleProject')
                return null
            }
        }
        return null
    }

    search(query, options = {}) {
        const rawQuery = (query ?? '').trim()
        if (!rawQuery) {
            if (this._searchDebounceTimer) {
                clearTimeout(this._searchDebounceTimer)
                this._searchDebounceTimer = null
            }
            if (this.state.storageType === 'remote') {
                this.state.searchQuery = ''
                this.state.searchResults = { projects: [], tracks: [] }
            }
            return { projects: [], tracks: [] }
        }

        const limit = Number.isInteger(options.limit) ? options.limit : 5

        if (this.state.storageType === 'remote') {
            const normalizedQuery = rawQuery.toLowerCase()
            if (normalizedQuery !== this.state.searchQuery || limit !== this.state.searchLimit) {
                this.state.searchQuery = normalizedQuery
                this.state.searchLimit = limit
                if (this._searchDebounceTimer) {
                    clearTimeout(this._searchDebounceTimer)
                }
                const debounceMs = Number.isInteger(options.debounceMs) ? options.debounceMs : 250
                if (debounceMs <= 0) {
                    this._searchRemote(normalizedQuery, limit)
                } else {
                    this._searchDebounceTimer = setTimeout(() => {
                        this._searchDebounceTimer = null
                        if (this.state.storageType === 'remote' && this.state.searchQuery === normalizedQuery && this.state.searchLimit === limit) {
                            this._searchRemote(normalizedQuery, limit)
                        }
                    }, debounceMs)
                }
            }
            return this.state.searchResults
        }

        const q = rawQuery.toLowerCase()
        const matchesQuery = (item) => (
            (item.name && item.name.toLowerCase().includes(q)) ||
            (item.artist && item.artist.toLowerCase().includes(q)) ||
            (item.key && item.key.toLowerCase().includes(q))
        )

        return {
            projects: this.state.projects.filter(matchesQuery).slice(0, limit),
            tracks: this.state.tracks.filter(matchesQuery).slice(0, limit)
        }
    }

    async _searchRemote(query, limit) {
        const requestId = ++this._searchRequestId
        try {
            if (!this.state.client) {
                this.state.client = new DemopartyClient('https://meow.demoparty.app', this.state.token, this.state.userId, this.state.username)
            }
            const response = await this.state.client.search(query, limit)
            if (requestId !== this._searchRequestId || this.state.searchQuery !== query) return
            this.state.searchResults = {
                projects: response?.projects ?? [],
                tracks: response?.tracks ?? []
            }
        } catch (error) {
            if (requestId !== this._searchRequestId) return
            this.logError(error, 'search')
        }
    }

    async fetchRemoteFile(fileId) {
        if (!fileId) {
            throw new Error('File ID is required')
        }
        if (this.state.storageType !== 'remote') {
            throw new Error('Remote storage is required to fetch files')
        }
        if (!this.state.client) {
            throw new Error('Remote client is not initialized')
        }
        return this.state.client.getFile(fileId)
    }

    async login(username, password) {
        console.log("Login", username)
        try {
            this.state.client = new DemopartyClient('https://meow.demoparty.app')
            let res = await this.state.client.login(username, password)
            if (res) {
                const token = res.newToken ?? res.token ?? res.user?.token ?? null
                if (!token) {
                    throw new Error('No token returned from login response')
                }
                this.state.token = token
                localStorage.setItem('demoparty_token', token)
                if (res.user?.id) {
                    this.state.userId = res.user.id
                    localStorage.setItem('demoparty_user_id', res.user.id)
                }
                if (res.user?.username) {
                    this.state.username = res.user.username
                    localStorage.setItem('demoparty_username', res.user.username)
                }
                localStorage.setItem('demoparty_storage_type', 'remote')
                this.state.storageType = 'remote'
                await this.checkAuth()
                await this.loadData()
                eventBus.notify('Logged in successfully!', 'Hello!');
                return true
            } else {
                return false
            }
        } catch (error) {
            this.logError(error, 'login')
            return false
        }
    }

    async loginWithToken(token) {
        console.log("Logging in with token");
        try {
            this.state.token = token;
            this.state.storageType = 'remote';
            this.state.isAuthorized = true;
            localStorage.setItem('demoparty_token', token);
            localStorage.setItem('demoparty_storage_type', 'remote');

            this.state.client = new DemopartyClient('https://meow.demoparty.app', token);
            const userProfile = await this.state.client.getUserProfile('me', { includeTracks: false });

            if (userProfile) {
                this.applyUserProfile(userProfile)
                await this.loadData();
                eventBus.notify('Logged in successfully!', 'Hello!');
                return true;
            } else {
                throw new Error("Failed to fetch user profile.");
            }
        } catch (error) {
            this.logError(error, 'loginWithToken');
            this.logout(); // Reset state on error
            return false;
        }
    }

    async loginWithMagicToken(magicToken) {
        console.log("Logging in with magic link");
        try {
            const client = new DemopartyClient('https://meow.demoparty.app');
            const res = await client.consumeMagicLink(magicToken);
            const token = res?.newToken ?? res?.token ?? res?.user?.token ?? null;
            if (!token) {
                throw new Error('No token returned from magic link response');
            }

            this.state.token = token;
            this.state.storageType = 'remote';
            this.state.isAuthorized = true;
            localStorage.setItem('demoparty_token', token);
            localStorage.setItem('demoparty_storage_type', 'remote');

            this.state.client = new DemopartyClient('https://meow.demoparty.app', token);
            if (res.user?.id) {
                this.state.userId = res.user.id;
                localStorage.setItem('demoparty_user_id', res.user.id);
            }
            if (res.user?.username) {
                this.state.username = res.user.username;
                localStorage.setItem('demoparty_username', res.user.username);
            }
            await this.loadData();
            eventBus.notify('Logged in successfully!', 'Hello!');
            return true;
        } catch (error) {
            this.logError(error, 'loginWithMagicToken');
            this.logout();
            return false;
        }
    }

    async requestPasswordReset(email) {
        try {
            if (!this.state.client) {
                this.state.client = new DemopartyClient('https://meow.demoparty.app')
            }
            return await this.state.client.requestMagicLink(email)
        } catch (error) {
            this.logError(error, 'requestPasswordReset')
            throw error
        }
    }


    async register(login, password) {
        console.log("Register", login)
        try {
            this.state.client = new DemopartyClient('https://meow.demoparty.app')
            let res = await this.state.client.createUser(login, password)
            if (res) {
                eventBus.notify('Registered successfully!', 'Now login please.');
                return true
            } else {
                return false
            }
        } catch (error) {
            this.logError(error, 'register')
            return false
        }
    }

    _buildTrackCreatePayload(track) {
        const normalizedTrack = this.normalizeTrack(track)
        if (!normalizedTrack?.file) {
            throw new Error('Track file is missing')
        }

        return {
            id: normalizedTrack.id,
            name: normalizedTrack.name,
            artist: normalizedTrack.artist,
            notes: normalizedTrack.notes ?? '',
            effects: normalizedTrack.effects ?? '',
            num: normalizedTrack.num ?? null,
            cover: this.getCoverId(normalizedTrack.cover),
            file: typeof normalizedTrack.file === 'object' ? normalizedTrack.file?.id : normalizedTrack.file,
            waveform: typeof normalizedTrack.waveform === 'object' ? normalizedTrack.waveform?.id : normalizedTrack.waveform,
            bpm: normalizedTrack.bpm ?? null,
            key: normalizedTrack.key ?? null,
            mode: normalizedTrack.mode ?? null,
            duration: normalizedTrack.duration ?? null,
            lufs: normalizedTrack.lufs ?? null,
            upload_date: normalizedTrack.upload_date ?? new Date().toISOString()
        }
    }

    _buildTrackUpdatePayload(track) {
        const normalizedTrack = this.normalizeTrack(track)
        return {
            id: normalizedTrack.id,
            name: normalizedTrack.name,
            artist: normalizedTrack.artist,
            notes: normalizedTrack.notes ?? '',
            effects: normalizedTrack.effects ?? '',
            num: normalizedTrack.num ?? null,
            cover: this.getCoverId(normalizedTrack.cover),
            bpm: normalizedTrack.bpm ?? null,
            key: normalizedTrack.key ?? null,
            mode: normalizedTrack.mode ?? null,
            duration: normalizedTrack.duration ?? null,
            lufs: normalizedTrack.lufs ?? null
        }
    }

    async _remoteTrackExists(trackId) {
        try {
            await this.state.client.getTrack(trackId)
            return true
        } catch (error) {
            if (error?.status === 404) {
                return false
            }
            throw error
        }
    }

    _replaceTrackAcrossState(track) {
        const normalizedTrack = this.normalizeTrack(track)
        let replacedInStandalone = false

        this.state.tracks = this.state.tracks.map(existingTrack => {
            if (existingTrack.id === normalizedTrack.id) {
                replacedInStandalone = true
                return normalizedTrack
            }
            return existingTrack
        })

        this.state.projects.forEach(project => {
            const trackIndex = (project.tracks || []).findIndex(existingTrack => existingTrack.id === normalizedTrack.id)
            if (trackIndex !== -1) {
                project.tracks.splice(trackIndex, 1, normalizedTrack)
            }
        })

        if (!replacedInStandalone) {
            this._indexTrack(normalizedTrack)
        } else {
            this._indexTrack(normalizedTrack)
        }

        return normalizedTrack
    }

    _persistTrackState() {
        if (this.state.storageType === 'local') {
            localStorage.setItem('tracks', JSON.stringify(this.state.tracks))
            localStorage.setItem('projects', JSON.stringify(this.state.projects))
        }
    }

    // + Updated
    async createTrack(data) {
        console.log("Created Track", data)
        this.state.isLoading = true
        const oldData = [...this.state.tracks]
        try {
            const normalizedTrack = this.normalizeTrack(data)
            this.state.tracks.push(normalizedTrack)
            this._persistTrackState()
            if (this.state.storageType === 'remote') {
                const dataForUpload = this._buildTrackCreatePayload(normalizedTrack)
                const response = await this.state.client.createTrack(dataForUpload)
                if (response?.track) {
                    this.state.tracks = this.state.tracks.map(track => track.id === normalizedTrack.id ? this.normalizeTrack(response.track) : track)
                }
            }
            this._rebuildTrackIndex()
            this.logSuccess('Track created successfully', normalizedTrack.name || 'Untitled track')
            this.state.isLoading = false
            return true
        } catch (error) {
            // TODO: Error handling
            this.logError(error, 'createTrack')
            this.state.tracks = oldData
            this.state.isLoading = false
            return false
        }
    }
    // + Updated
    async updateTrack(data) {
        console.log("Update Track", data)
        this.state.isLoading = true
        const oldTracksData = [...this.state.tracks]
        const oldProjectsData = this.state.projects.map(p => ({ ...p, tracks: [...(p.tracks || [])] }))
        try {
            const normalizedTrack = this.normalizeTrack(data)
            this._replaceTrackAcrossState(normalizedTrack)
            this._persistTrackState()
            if (this.state.storageType === 'remote') {
                const dataForUpload = this._buildTrackUpdatePayload(normalizedTrack)
                console.log("Data for upload", dataForUpload)
                await this.state.client.updateTrack(data.id, dataForUpload)
            }
            this._rebuildTrackIndex()
            this.state.isLoading = false
            return true
        } catch (error) {
            // TODO: Error handling
            this.logError(error, 'updateTrack')
            this.state.tracks = oldTracksData
            this.state.projects = oldProjectsData
            this.state.isLoading = false
            return false
        }
    }
    // + Updated
    async deleteTrack(id) {
        console.log("Delete Track", id)
        this.state.isLoading = true
        const oldTracksData = [...this.state.tracks]
        const oldProjectsData = this.state.projects.map(p => ({ ...p, tracks: [...(p.tracks || [])] }))
        try {
            // Remove track from tracks array
            this.state.tracks = this.state.tracks.filter(track => track.id !== id)
            // Remove track from any projects that contain it
            this.state.projects.forEach(project => {
                project.tracks = (project.tracks || []).filter(track => track.id !== id)
                project.track_count = project.tracks.length
            })
            if (this.state.storageType === 'local') {
                this._persistTrackState()
            }
            if (this.state.storageType === 'remote') {
                await this.state.client.deleteTrack(id)
            }
            this._unindexTrack(id)
            this.state.isLoading = false
            return true
        } catch (error) {
            // TODO: Error handling
            this.logError(error, 'deleteTrack')
            this.state.tracks = oldTracksData
            this.state.projects = oldProjectsData
            this.state.isLoading = false
            return false
        }
    }

    // + Updated
    async createProject(data) {
        console.log("Create Project", data)
        this.state.isLoading = true
        const oldData = [...this.state.projects]
        try {
            const nextProject = this.normalizeProject({
                ...data,
                sort_order: this.state.projects.length + 1
            }, this.state.projects.length + 1)
            this.state.projects = this.resequenceProjects([...this.state.projects, nextProject])
            if (this.state.storageType === 'local') {
                localStorage.setItem('projects', JSON.stringify(this.state.projects))
            }
            if (this.state.storageType === 'remote') {
                const dataForUpload = {
                    ...data,
                    cover: this.getCoverId(data.cover),
                    isPublic: this.getProjectPublicValue(data),
                    tracks: (data.tracks || []).map(track =>
                        typeof track === 'object' ? track.id : track
                    )
                }
                delete dataForUpload.public
                await this.state.client.createProject(dataForUpload)
            }
            this.state.isLoading = false
            return true
        } catch (error) {
            // TODO: Error handling
            this.logError(error, 'createProject')
            this.state.projects = oldData
            this.state.isLoading = false
            return false
        }
    }
    // + Updated
    async updateProject(project) {
        console.log("Updating Project", project)
        this.state.isLoading = true
        const oldProjectsData = this.state.projects.map(p => ({ ...p, tracks: [...(p.tracks || [])] }))
        const oldTracksData = [...this.state.tracks]
        try {
            let normalizedProject = this.normalizeProject(project)
            if (this.state.storageType === 'remote' && normalizedProject.artist == null) {
                const loadedProject = await this.loadSingleProject(normalizedProject.id)
                if (loadedProject) {
                    const mergedProject = {
                        ...loadedProject,
                        ...normalizedProject
                    }
                    if (!this.hasLoadedProjectTracks(normalizedProject)) {
                        mergedProject.tracks = loadedProject.tracks || []
                        mergedProject.track_count = Number.isFinite(loadedProject.track_count)
                            ? loadedProject.track_count
                            : (mergedProject.tracks || []).length
                    }
                    normalizedProject = this.normalizeProject(mergedProject)
                }
            }
            // Заменить существующий project на новую версию в this.state.projects
            this.state.projects = this.resequenceProjects(
                this.state.projects.map(p => p.id === normalizedProject.id ? normalizedProject : p)
            )
            let newData = {
                ...normalizedProject,
                cover: this.getCoverId(normalizedProject.cover),
                isPublic: this.getProjectPublicValue(normalizedProject),
                tracks: normalizedProject.tracks ? [...normalizedProject.tracks] : []
            }
            delete newData.public
            const shouldUpdateTracks = this.state.storageType !== 'remote' || this.hasLoadedProjectTracks(normalizedProject)
            if (shouldUpdateTracks) {
                // Отправляем на бэкенд только массив ID треков
                newData.tracks = (newData.tracks || []).map(track =>
                    typeof track === 'object' ? track.id : track
                )
                // Удались из state.tracks треки, которые теперь в проекте
                this.state.tracks = this.state.tracks.filter(track => !newData.tracks.includes(track.id))
            } else {
                delete newData.tracks
            }
            if (this.state.storageType === 'local') {
                localStorage.setItem('projects', JSON.stringify(this.state.projects))
                localStorage.setItem('tracks', JSON.stringify(this.state.tracks))
            }
            if (this.state.storageType === 'remote') {
                await this.state.client.updateProject(newData.id, newData)
            }
            this.state.isLoading = false
            return true
        } catch (error) {
            // TODO: Error handling
            this.logError(error, 'deleteProject')
            this.state.projects = oldProjectsData
            this.state.tracks = oldTracksData
            this.state.isLoading = false
            return false
        }
    }
    // + Updated
    async deleteProject(id) {
        console.log("Deleted Project", id)
        this.state.isLoading = true
        const oldProjectsData = this.state.projects.map(p => ({ ...p, tracks: [...(p.tracks || [])] }))
        const oldTracksData = [...this.state.tracks]
        try {
            const projectToDelete = this.state.projects.find(project => project.id === id)
            // Return tracks to general tracks list
            if (projectToDelete && projectToDelete.tracks && projectToDelete.tracks.length > 0) {
                const existingTrackIds = this.state.tracks.map(track => track.id)
                projectToDelete.tracks.forEach(track => {
                    if (!existingTrackIds.includes(track.id)) {
                        this.state.tracks.push(track)
                    }
                })
            }
            // Remove project from projects array
            this.state.projects = this.resequenceProjects(
                this.state.projects.filter(project => project.id !== id)
            )

            if (this.state.storageType === 'local') {
                localStorage.setItem('projects', JSON.stringify(this.state.projects))
                localStorage.setItem('tracks', JSON.stringify(this.state.tracks))
            }
            if (this.state.storageType === 'remote') {
                await this.state.client.deleteProject(id)
            }
            this.state.isLoading = false
            return true
        } catch (error) {
            // TODO: Error handling
            this.logError(error, 'updateProject')
            this.state.projects = oldProjectsData
            this.state.tracks = oldTracksData
            this.state.isLoading = false
            return false
        }
    }
    // + Updated
    async reorderProjects(orderedProjectIds) {
        if (!Array.isArray(orderedProjectIds) || orderedProjectIds.length !== this.state.projects.length) {
            return false
        }
        this.state.isLoading = true
        const oldProjectsData = this.state.projects.map(p => ({ ...p, tracks: [...(p.tracks || [])] }))
        try {
            const projectMap = new Map(this.state.projects.map(project => [project.id, project]))
            const reorderedProjects = orderedProjectIds
                .map(projectId => projectMap.get(projectId))
                .filter(Boolean)

            if (reorderedProjects.length !== this.state.projects.length) {
                throw new Error('Project reorder list is incomplete')
            }

            this.state.projects = this.resequenceProjects(reorderedProjects)

            if (this.state.storageType === 'local') {
                localStorage.setItem('projects', JSON.stringify(this.state.projects))
            }
            if (this.state.storageType === 'remote') {
                if (!this.state.client) {
                    this.state.client = new DemopartyClient('https://meow.demoparty.app', this.state.token, this.state.userId, this.state.username)
                }
                await this.state.client.reorderProjects(this.state.projects.map(project => project.id))
            }

            this.state.isLoading = false
            return true
        } catch (error) {
            this.logError(error, 'reorderProjects')
            this.state.projects = oldProjectsData
            this.state.isLoading = false
            return false
        }
    }
    // + Updated
    switchStorages() {
        console.log("Switching Storages")
        this.state.storageType = this.state.storageType === 'local' ? 'remote' : 'local'
        localStorage.setItem('demoparty_storage_type', this.state.storageType)
        this.checkAuth()
        this.loadData()
    }
    // + Updated
    // Function probably useless for remote storage
    async getFile(id) {
        console.log("Getting file", id)
        if (!id) {
            return null
        }
        this.state.isLoading = true
        try {
            let result = null
            if (this.state.storageType === 'local') {
                result = await getFile(id);
                if (result && result.data) {
                    result = result.data;
                }
            }
            if (this.state.storageType === 'remote') {
                // do nothing
            }
            this.state.isLoading = false
            return result;
        } catch (error) {
            // TODO: Error handling
            this.logError(error, 'getFile')
            this.state.isLoading = false
            return false
        }
    }

    _getLocalStorageSnapshot() {
        let tracks = []
        let projects = []
        try {
            tracks = this.normalizeTracks(JSON.parse(localStorage.getItem('tracks')) || [])
        } catch (error) {
            console.warn('Failed to parse local tracks', error)
        }
        try {
            projects = this.normalizeProjects(JSON.parse(localStorage.getItem('projects')) || [])
        } catch (error) {
            console.warn('Failed to parse local projects', error)
        }
        return { tracks, projects }
    }

    isTrackInLocalStorage(trackId) {
        if (!trackId) return false
        const { tracks, projects } = this._getLocalStorageSnapshot()
        if (tracks.some(track => track.id === trackId)) {
            return true
        }
        return projects.some(project => (project.tracks || []).some(track => track.id === trackId))
    }

    async _getLocalFileData(fileId) {
        if (!fileId) return null
        const stored = await getFile(fileId)
        return stored?.data ?? null
    }

    async _saveFileIfMissing(fileId, data) {
        if (!fileId) return false
        const existing = await getFile(fileId)
        if (existing) return false
        await saveFile({ id: fileId, data })
        return true
    }

    async _normalizeToBlob(fileData) {
        if (!fileData) return null
        if (fileData instanceof Blob) return fileData
        if (fileData instanceof ArrayBuffer) {
            return new Blob([fileData], { type: 'application/octet-stream' })
        }
        if (ArrayBuffer.isView(fileData)) {
            return new Blob([fileData.buffer], { type: 'application/octet-stream' })
        }
        if (typeof fileData === 'string') {
            if (fileData.startsWith('data:')) {
                const response = await fetch(fileData)
                return await response.blob()
            }
            return new Blob([fileData], { type: 'application/octet-stream' })
        }
        return null
    }

    async _fetchArrayBuffer(url) {
        if (!url) return null
        const response = await fetch(url)
        if (!response.ok) {
            throw new Error(`Failed to fetch file: ${response.status}`)
        }
        return await response.arrayBuffer()
    }

    async saveTrackToLocal(trackId) {
        if (!trackId) return false
        if (this.isTrackInLocalStorage(trackId)) {
            this.logInfo('Local copy already exists', 'Track is already saved locally.')
            return true
        }
        if (this.state.storageType !== 'remote') {
            this.logWarning('Remote storage required', 'Switch to remote storage to save a cloud track locally.')
            return false
        }
        try {
            await initDB()
            let track = this.getTrackById(trackId)
            if (!track) {
                track = await this.loadSingleTrack(trackId)
            }
            if (!track) {
                throw new Error('Track not found')
            }
            const normalizedTrack = this.normalizeTrack(track)
            const fileId = typeof normalizedTrack.file === 'object' ? normalizedTrack.file?.id : normalizedTrack.file
            const waveformId = typeof normalizedTrack.waveform === 'object' ? normalizedTrack.waveform?.id : normalizedTrack.waveform
            if (!fileId) {
                throw new Error('Track file is missing')
            }

            const fileUrl = await this.getFileLink(fileId)
            if (!fileUrl) {
                throw new Error('Failed to get file URL')
            }
            const fileData = await this._fetchArrayBuffer(fileUrl)
            await this._saveFileIfMissing(fileId, fileData)

            if (waveformId) {
                const waveformUrl = await this.getFileLink(waveformId)
                if (!waveformUrl) {
                    throw new Error('Failed to get waveform URL')
                }
                const waveformData = await this._fetchArrayBuffer(waveformUrl)
                await this._saveFileIfMissing(waveformId, waveformData)
            }

            const { tracks, projects } = this._getLocalStorageSnapshot()
            const existsInProjects = projects.some(project => (project.tracks || []).some(t => t.id === trackId))
            if (!tracks.some(t => t.id === trackId) && !existsInProjects) {
                const localTrack = this.normalizeTrack({
                    ...normalizedTrack,
                    file: fileId,
                    waveform: waveformId
                })
                tracks.push(localTrack)
                localStorage.setItem('tracks', JSON.stringify(tracks))
            }
            this.logSuccess('Saved to local storage', normalizedTrack.name || normalizedTrack.filename || 'Track')
            return true
        } catch (error) {
            this.logError(error, 'saveTrackToLocal')
            return false
        }
    }

    async saveTrackToCloud(trackId) {
        if (!trackId) return false
        if (!this.state.isAuthorized) {
            this.logWarning('Login required', 'Please sign in to save tracks to the cloud.')
            return false
        }
        try {
            if (!this.state.client) {
                this.state.client = new DemopartyClient('https://meow.demoparty.app', this.state.token, this.state.userId, this.state.username)
            }
            let track = this.getTrackById(trackId)
            if (!track) {
                const { tracks, projects } = this._getLocalStorageSnapshot()
                track = tracks.find(t => t.id === trackId)
                if (!track) {
                    for (const project of projects) {
                        const found = (project.tracks || []).find(t => t.id === trackId)
                        if (found) {
                            track = found
                            break
                        }
                    }
                }
            }
            if (!track) {
                throw new Error('Track not found')
            }
            const normalizedTrack = this.normalizeTrack(track)
            const remoteExists = await this._remoteTrackExists(trackId)

            if (remoteExists) {
                await this.state.client.updateTrack(trackId, this._buildTrackUpdatePayload(normalizedTrack))
                this.logSuccess('Saved to cloud', normalizedTrack.name || normalizedTrack.filename || 'Track')
                return true
            }

            await initDB()
            const fileId = typeof normalizedTrack.file === 'object' ? normalizedTrack.file?.id : normalizedTrack.file
            const waveformId = typeof normalizedTrack.waveform === 'object' ? normalizedTrack.waveform?.id : normalizedTrack.waveform
            if (!fileId) {
                throw new Error('Track file is missing')
            }

            const fileData = await this._getLocalFileData(fileId)
            if (!fileData) {
                throw new Error('Local file data not found')
            }
            const fileBlob = await this._normalizeToBlob(fileData)
            const filename = normalizedTrack.filename || normalizedTrack.name || 'track'
            await this.state.client.uploadFilePresigned(fileId, fileBlob, filename)

            if (waveformId) {
                const waveformData = await this._getLocalFileData(waveformId)
                if (waveformData) {
                    const waveformBlob = await this._normalizeToBlob(waveformData)
                    await this.state.client.uploadFilePresigned(waveformId, waveformBlob, `waveform_${filename}.png`)
                }
            }

            await this.state.client.createTrack(this._buildTrackCreatePayload(normalizedTrack))

            this.logSuccess('Saved to cloud', normalizedTrack.name || normalizedTrack.filename || 'Track')
            return true
        } catch (error) {
            this.logError(error, 'saveTrackToCloud')
            return false
        }
    }
    // + Updated
    async uploadFile(arrayBuffer, filename) {
        this.state.isLoading = true
        const id = uuidv4();
        try {
            if (this.state.storageType === 'local') {
                const fileData = {
                    id: id,
                    data: arrayBuffer
                }
                await saveFile(fileData)
                this.state.isLoading = false
                return id
            }
            if (this.state.storageType === 'remote') {
                const file = new Blob([arrayBuffer], { type: 'application/octet-stream' })
                const uploadFile = await this.state.client.uploadFilePresigned(id, file, filename)
                console.log("Uploaded file res", uploadFile)
                this.state.isLoading = false
                return uploadFile
            }
        } catch (error) {
            // TODO: Error handling
            this.logError(error, 'uploadFile')
            this.state.isLoading = false
            throw error
        }
    }
    // + Updated
    getCoverId(cover) {
        if (!cover) return null
        if (typeof cover === 'object') {
            return cover.id || null
        }
        if (typeof cover === 'string' && cover.trim() !== '') {
            return cover
        }
        return null
    }
    // + Updated
    // Get signed URL for a file by its ID (with caching for remote storage)
    async getFileLink(fileId) {
        if (!fileId) return null
        // Extract ID if object is passed
        const id = typeof fileId === 'object' ? (fileId.id || null) : fileId
        if (!id) return null

        if (this.state.storageType === 'local') {
            // For local storage, get file data and create blob URL
            const fileData = await this.getFile(id)
            return this.resolveFileUrl(fileData)
        }

        if (this.state.storageType === 'remote' || this.state.storageType === 'shared') {
            // Check cache first
            const cached = this._urlCache.get(id)
            if (cached && cached.expiresAt > Date.now()) {
                return cached.url
            }

            // Fetch new signed URL
            if (!this.state.client) {
                this.state.client = new DemopartyClient('https://meow.demoparty.app', this.state.token, this.state.userId, this.state.username)
            }
            try {
                const response = await this.state.client.getFileUrl(id)
                const url = response.link

                // Store in cache
                this._urlCache.set(id, {
                    url: url,
                    expiresAt: Date.now() + this._urlCacheTTL
                })

                return url
            } catch (error) {
                this.logError(error, 'getFileLink')
                return null
            }
        }
        return null
    }

    // Clear URL cache (call on logout or when needed)
    clearUrlCache() {
        this._urlCache.clear()
    }

    // Get file metadata (size, filename, etc.) from remote storage
    async getFileInfo(fileId) {
        if (!fileId || this.state.storageType !== 'remote') return null
        if (!this.state.client) {
            this.state.client = new DemopartyClient('https://meow.demoparty.app', this.state.token, this.state.userId, this.state.username)
        }
        try {
            return await this.state.client.getFileUrl(fileId)
        } catch (error) {
            this.logError(error, 'getFileInfo')
            return null
        }
    }

    // + Updated
    async getCoverUrl(cover) {
        if (!cover) return null
        // For local storage with string ID
        if (this.state.storageType === 'local') {
            const fileData = await this.getFile(cover)
            return this.resolveFileUrl(fileData)
        }
        // For remote/shared storage - get signed URL by ID
        const coverId = typeof cover === 'object' ? cover.id : cover
        if (!coverId) return null
        return this.getFileLink(coverId)
    }
    // + Updated
    resolveFileUrl(fileData) {
        if (!fileData) return null
        if (typeof fileData === 'string') {
            return fileData
        }
        let url = null
        if (fileData instanceof Blob) {
            url = URL.createObjectURL(fileData)
        } else if (fileData instanceof ArrayBuffer) {
            url = URL.createObjectURL(new Blob([fileData]))
        }
        if (url) {
            this._blobUrls.add(url)
        }
        return url
    }
    // Revoke a specific Blob URL to free memory
    revokeBlobUrl(url) {
        if (url && url.startsWith('blob:') && this._blobUrls.has(url)) {
            URL.revokeObjectURL(url)
            this._blobUrls.delete(url)
        }
    }
    // Revoke all tracked Blob URLs (call on logout or major state reset)
    revokeAllBlobUrls() {
        for (const url of this._blobUrls) {
            URL.revokeObjectURL(url)
        }
        this._blobUrls.clear()
    }
    // + Updated
    async deleteCoverImage(cover) {
        const coverId = this.getCoverId(cover)
        if (!coverId) return false
        try {
            if (this.state.storageType === 'local') {
                await deleteFile(coverId)
                return true
            }
        } catch (error) {
            this.logError(error, 'deleteCoverImage')
        }
        return false
    }
    // + Updated
    async deleteCoverImageIfReplaced(oldCover, newCover) {
        const oldId = this.getCoverId(oldCover)
        const newId = this.getCoverId(newCover)
        if (!oldId || oldId === newId) return false
        return this.deleteCoverImage(oldId)
    }
    // + Updated
    async reorderProjectTracks(projectId, orderedTrackIds) {
        if (!projectId || !Array.isArray(orderedTrackIds) || orderedTrackIds.length === 0) {
            return false
        }
        this.state.isLoading = true
        const oldProjectsData = this.state.projects.map(p => ({ ...p, tracks: [...(p.tracks || [])] }))
        const oldTracksData = [...this.state.tracks]
        try {
            const project = await this.ensureProjectTracksLoaded(projectId)
            if (!project) {
                throw new Error('Project not found')
            }

            const trackMap = new Map((project.tracks || []).map(track => [track.id, track]))
            const reordered = []
            const updatedById = new Map()

            orderedTrackIds.forEach((trackId, index) => {
                const track = trackMap.get(trackId)
                if (!track) return
                const updated = { ...track, num: String(index + 1) }
                reordered.push(updated)
                updatedById.set(trackId, updated)
            })

            const remaining = (project.tracks || []).filter(track => !updatedById.has(track.id))
            remaining.forEach((track, idx) => {
                const updated = { ...track, num: String(reordered.length + idx + 1) }
                updatedById.set(track.id, updated)
                reordered.push(updated)
            })

            project.tracks = reordered
            project.track_count = project.tracks.length

            this.state.tracks = this.state.tracks.map(track => updatedById.get(track.id) || track)

            if (this.state.storageType === 'local') {
                localStorage.setItem('projects', JSON.stringify(this.state.projects))
                localStorage.setItem('tracks', JSON.stringify(this.state.tracks))
            }
            if (this.state.storageType === 'remote') {
                await this.state.client.reorderProject(projectId, orderedTrackIds)
            }

            this.state.isLoading = false
            return true
        } catch (error) {
            this.logError(error, 'reorderProjectTracks')
            this.state.projects = oldProjectsData
            this.state.tracks = oldTracksData
            this.state.isLoading = false
            return false
        }
    }
    // + Updated
    async addTrackToProject(trackId, projectId) {
        console.log("Adding track to project", trackId, projectId)
        this.state.isLoading = true
        const oldTracksData = [...this.state.tracks]
        const oldProjectsData = this.state.projects.map(p => ({ ...p, tracks: [...(p.tracks || [])] }))
        try {
            const project = await this.ensureProjectTracksLoaded(projectId)
            const track = this.state.tracks.find(t => t.id === trackId)
            if (!project || !track) {
                throw new Error('Project or track not found')
            }
            // Check if track is already in project
            if (project.tracks.some(t => t.id === trackId)) {
                throw new Error('Track already in project')
            }
            // Add track to project
            project.tracks.push(track)
            project.track_count = project.tracks.length
            // Remove track from general tracks list
            this.state.tracks = this.state.tracks.filter(t => t.id !== trackId)
            if (this.state.storageType === 'local') {
                localStorage.setItem('projects', JSON.stringify(this.state.projects))
                localStorage.setItem('tracks', JSON.stringify(this.state.tracks))
            }
            if (this.state.storageType === 'remote') {
                // Send only track IDs to backend
                const backendProjectData = {
                    name: project.name,
                    artist: project.artist,
                    cover: typeof project.cover === 'object' ? project.cover?.id : project.cover,
                    isPublic: project.public,
                    tracks: project.tracks.map(t => t.id)
                }
                await this.state.client.updateProject(project.id, backendProjectData)
            }

            this.state.isLoading = false
            return true
        } catch (error) {
            // TODO: Error handling
            this.logError(error, 'addTrackToProject')
            this.state.tracks = oldTracksData
            this.state.projects = oldProjectsData
            this.state.isLoading = false
            return false
        }
    }
    // + Updated
    async moveTrackToProject(trackId, targetProjectId) {
        console.log("Moving track to project", trackId, targetProjectId)
        this.state.isLoading = true
        const oldTracksData = [...this.state.tracks]
        const oldProjectsData = this.state.projects.map(p => ({ ...p, tracks: [...(p.tracks || [])] }))
        try {
            const targetProject = await this.ensureProjectTracksLoaded(targetProjectId)
            if (!targetProject) {
                throw new Error('Target project not found')
            }
            // Check if track is already in target project
            if (targetProject.tracks.some(t => t.id === trackId)) {
                throw new Error('Track already in target project')
            }
            let track = null
            let sourceProject = null
            // First try to find track in general tracks list
            track = this.state.tracks.find(t => t.id === trackId)
            if (track) {
                // Track is in general tracks list, just add it to project
                targetProject.tracks.push(track)
                targetProject.track_count = targetProject.tracks.length
                this.state.tracks = this.state.tracks.filter(t => t.id !== trackId)
                if (this.state.storageType === 'local') {
                    localStorage.setItem('projects', JSON.stringify(this.state.projects))
                    localStorage.setItem('tracks', JSON.stringify(this.state.tracks))
                }
                if (this.state.storageType === 'remote') {
                    const backendProjectData = {
                        name: targetProject.name,
                        artist: targetProject.artist,
                        cover: typeof targetProject.cover === 'object' ? targetProject.cover?.id : targetProject.cover,
                        isPublic: targetProject.public,
                        tracks: targetProject.tracks.map(t => t.id)
                    }
                    await this.state.client.updateProject(targetProject.id, backendProjectData)
                }
                this.state.isLoading = false
                return true
            } else {
                sourceProject = this.state.projects.find(p => p.tracks.some(t => t.id === trackId))
                if (!sourceProject) {
                    throw new Error('Track not found')
                }
                const trackIndex = sourceProject.tracks.findIndex(t => t.id === trackId)
                track = sourceProject.tracks[trackIndex]
                // Remove from source project
                sourceProject.tracks.splice(trackIndex, 1)
                sourceProject.track_count = sourceProject.tracks.length
                // Add to target project
                targetProject.tracks.push(track)
                targetProject.track_count = targetProject.tracks.length

                if (this.state.storageType === 'local') {
                    localStorage.setItem('projects', JSON.stringify(this.state.projects))
                }

                if (this.state.storageType === 'remote') {
                    await this.state.client.moveProjectTrack(sourceProject.id, targetProject.id, trackId)
                }
                this.state.isLoading = false
                return true
            }
        } catch (error) {
            // TODO: Error handling
            this.logError(error, 'moveTrackToProject')
            this.state.tracks = oldTracksData
            this.state.projects = oldProjectsData
            this.state.isLoading = false
            return false
        }
    }
    // + Updated
    async removeTrackFromProject(trackId, projectId) {
        console.log("Removing track from project", trackId, projectId)
        this.state.isLoading = true
        const oldTracksData = [...this.state.tracks]
        const oldProjectsData = this.state.projects.map(p => ({ ...p, tracks: [...(p.tracks || [])] }))
        try {
            const project = await this.ensureProjectTracksLoaded(projectId)
            if (!project) {
                throw new Error('Project not found')
            }
            const trackIndex = project.tracks.findIndex(t => t.id === trackId)
            if (trackIndex === -1) {
                throw new Error('Track not found in project')
            }
            const removedTrack = project.tracks[trackIndex]
            project.tracks.splice(trackIndex, 1)
            project.track_count = project.tracks.length
            this.state.tracks.push(removedTrack)
            if (this.state.storageType === 'local') {
                localStorage.setItem('projects', JSON.stringify(this.state.projects))
                localStorage.setItem('tracks', JSON.stringify(this.state.tracks))
            }
            if (this.state.storageType === 'remote') {
                // Update project on backend with track IDs only
                const backendProjectData = {
                    name: project.name,
                    artist: project.artist,
                    cover: typeof project.cover === 'object' ? project.cover?.id : project.cover,
                    isPublic: project.public,
                    tracks: project.tracks.map(t => t.id)
                }
                await this.state.client.updateProject(project.id, backendProjectData)
            }
            this.state.isLoading = false
            return true
        } catch (error) {
            // TODO: Error handling
            this.logError(error, 'removeTrackFromProject')
            this.state.tracks = oldTracksData
            this.state.projects = oldProjectsData
            this.state.isLoading = false
            return false
        }
    }
    // Rebuild the track index from current state
    _rebuildTrackIndex() {
        this._trackIndex.clear()
        // Index tracks from projects
        for (const project of this.state.projects) {
            for (const track of (project.tracks || [])) {
                this._trackIndex.set(track.id, track)
            }
        }
        // Index shared tracks
        for (const track of this.state.sharedTracks) {
            this._trackIndex.set(track.id, track)
        }
        // Index standalone tracks
        for (const track of this.state.tracks) {
            this._trackIndex.set(track.id, track)
        }
    }

    // Add a track to the index
    _indexTrack(track) {
        if (track && track.id) {
            this._trackIndex.set(track.id, track)
        }
    }

    // Remove a track from the index
    _unindexTrack(trackId) {
        this._trackIndex.delete(trackId)
    }

    // O(1) track lookup by ID
    getTrackById(id) {
        if (!id) return null
        // Try index first (O(1))
        const indexed = this._trackIndex.get(id)
        if (indexed) return indexed
        // Fallback to linear search and index the result
        for (const project of this.state.projects) {
            for (const track of (project.tracks || [])) {
                if (track.id === id) {
                    this._indexTrack(track)
                    return track
                }
            }
        }
        for (const track of this.state.sharedTracks) {
            if (track.id === id) {
                this._indexTrack(track)
                return track
            }
        }
        for (const track of this.state.tracks) {
            if (track.id === id) {
                this._indexTrack(track)
                return track
            }
        }
        return null
    }
    createPreset(data) {
        console.log("Created Preset", data)
        this.state.presets.push(data)
        localStorage.setItem('presets', JSON.stringify(this.state.presets))
    }
    updatePreset(data) {
        console.log("Updated Preset", data)
        this.state.presets = this.state.presets.map(preset => preset.id === data.id ? data : preset)
        localStorage.setItem('presets', JSON.stringify(this.state.presets))
    }
    deletePreset(id) {
        console.log("Deleted Preset", id)
        this.state.presets = this.state.presets.filter(preset => preset.id !== id)
        localStorage.setItem('presets', JSON.stringify(this.state.presets))
    }
    async storageInfo() {
        return await this.state.client.storageInfo()
    }

    logError(error, context) {
        console.error(`Error in ${context}:`, error)
        const { title, description } = getUserErrorMessage(error, context)
        eventBus.notify(title, description)
    }

    // Helper method for success notifications
    logSuccess(message, description = '') {
        console.log(`Success: ${message}`, description)
        eventBus.notify(message, description)
    }

    // Helper method for info notifications
    logInfo(message, description = '') {
        console.log(`Info: ${message}`, description)
        eventBus.notify(message, description)
    }

    // Helper method for warning notifications
    logWarning(message, description = '') {
        console.warn(`Warning: ${message}`, description)
        eventBus.notify(message, description)
    }
    async createLink(type, objectId) {
        const oldLinks = [...this.state.publicLinks]
        console.log("Creating Link", type, objectId)
        try {
            if( this.state.storageType !== 'remote') {
                console.log("Links can be created only in remote storage mode.")
                return false
            }
            const res = await this.state.client.createLink(type, objectId)
            if (res) {
                this.state.publicLinks.push(res)
                return res
            }
            return false
        } catch (error) {
            this.state.publicLinks = oldLinks
            // TODO: Error handling
            this.logError(error, 'createLink')
            return false
        }

    }
    async deleteLink(linkId) {
        const oldLinks = [...this.state.publicLinks]
        console.log("Deleting Link", linkId)
        try {
            if( this.state.storageType !== 'remote') {
                console.log("Links can be deleted only in remote storage mode.")
                return false
            }
            const res = await this.state.client.deleteLink(linkId)
            if (res) {
                this.state.publicLinks = this.state.publicLinks.filter(link => link.id !== linkId)
                return true
            }
            return false
        } catch (error) {
            this.state.publicLinks = oldLinks
            // TODO: Error handling
            this.logError(error, 'deleteLink')
            return false
        }
    }
    async getLink(linkId) {
        if(this.state.client === null) {
            console.log("No client singleton, creating new one")
            this.state.client = new DemopartyClient('https://meow.demoparty.app')
        }
        console.log("Getting Link", linkId)
        try {
            const res = await this.state.client.getLink(linkId)
            const preserveRemoteAuth = this.state.storageType === 'remote' && this.state.isAuthorized
            if (!preserveRemoteAuth) {
                this.state.storageType = 'shared'
            }
            if(res && res.project){
                const sharedProject = this.normalizeProject(res.project)
                res.project = sharedProject
                this.state.sharedProjects = [
                    ...this.state.sharedProjects.filter(project => project.id !== sharedProject.id),
                    sharedProject
                ]
                const sharedTrackIds = new Set(sharedProject.tracks.map(track => track.id))
                this.state.sharedTracks = [
                    ...this.state.sharedTracks.filter(track => !sharedTrackIds.has(track.id)),
                    ...sharedProject.tracks
                ]
            }
            if(res && res.track){
                const sharedTrack = this.normalizeTrack(res.track)
                res.track = sharedTrack
                this.state.sharedTracks = [
                    ...this.state.sharedTracks.filter(track => track.id !== sharedTrack.id),
                    sharedTrack
                ]
            }
            this._rebuildTrackIndex()
            return res
        } catch (error) {
            // TODO: Error handling
            this.logError(error, 'getLink')
            return null
        }
    }
    async createSubscriptionCheckout(plan){
        return this.state.client.createSubscriptionCheckout(plan)
    }

    async getBillingPortal(){
        return this.state.client.getBillingPortal()
    }

    // Check if user has an active subscription
    // Subscription is active only if status is "active"
    hasActiveSubscription() {
        const sub = this.state.subscription
        if (!sub) return false

        return sub.status === 'active'
    }
}

const storageService = new StorageService();
export default storageService;
